import { createAsyncThunk } from "@reduxjs/toolkit";
import { captureException } from "@sentry/browser";

import * as basketAPI from "../../../api/basket";
import * as financingAPI from "../../../api/financing";
import * as wishlistsAPI from "../../../api/wishlists";
import { IBasket } from "../../../models/catalogue";
import { FinancingPlan } from "../../../models/financing";
import { LIST_PREVIOUSLY_IN_BASKET } from "../constants";
import { filterFinancingPlans, sortFinancingPlans } from "../utils";

/**
 * Return type for loadBasket thunk.
 */
interface LoadBasketResult {
    basket: IBasket;
    previousItemsList: ReturnType<
        typeof wishlistsAPI.listLines
    > extends Promise<infer T>
        ? T
        : never;
    financingPlans: FinancingPlan[];
}

/**
 * Load basket, wishlists, and financing plans.
 *
 * This is the main data loading operation for initializing the checkout flow.
 * Fetches the current shopping basket, previously-in-basket wishlist items,
 * and available financing plans. Also triggers loading of the favorites wishlist.
 *
 * @returns {Promise<LoadBasketResult>} Promise resolving to basket, wishlist items, and financing plans
 *
 * @example
 * // Load basket on checkout page mount
 * dispatch(loadBasket());
 *
 * @example
 * // Reload basket after item added
 * await dispatch(loadBasket()).unwrap();
 *
 * @sideEffects
 * - Updates `data.basket` with fetched basket data via extraReducers
 * - Updates `data.wishlists[LIST_PREVIOUSLY_IN_BASKET]` with wishlist items via extraReducers
 * - Updates `data.financing_plans` with filtered and sorted plans via extraReducers
 * - Dispatches `loadFavorites` thunk to load favorites wishlist
 * - Logs to console in debug mode
 */
export const loadBasket = createAsyncThunk(
    "checkout/loadBasket",
    async (_, { dispatch }): Promise<LoadBasketResult> => {
        // Load the basket
        const loadingBasket = basketAPI.load();
        // Load wishlist lines, but allow this to fail gracefully
        const loadingPrevious = wishlistsAPI
            .listLines(LIST_PREVIOUSLY_IN_BASKET)
            .catch((err) => {
                captureException(err);
                return [];
            });
        // Load Financing plans, but allow this to fail gracefully
        const loadingPlans = financingAPI.listFinancingPlans().catch((err) => {
            captureException(err);
            return [];
        });

        console.debug("Loading basket...");
        const [basket, previousItemsList, financingPlans] = await Promise.all([
            loadingBasket,
            loadingPrevious,
            loadingPlans,
        ]);

        const sortedPlans = sortFinancingPlans(
            filterFinancingPlans(financingPlans),
        );

        // Also load favorites (imported dynamically to avoid circular dependency)
        const { loadFavorites } = await import("./loadFavorites");
        dispatch(loadFavorites());

        console.debug("Loaded basket.");

        return {
            basket,
            previousItemsList,
            financingPlans: sortedPlans,
        };
    },
);
